/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { message, Spin } from 'antd';
import patientApi from '../../api/patient.api';
import { preventiveApi } from '../../api/preventive.api';
import { PageShell, Card, Section, Kpi, StatusPill, EmptyState } from '../../ui';

// NHIS plan-type taxonomy.
const PLAN_TYPE_LABEL = {
  A1:    'Годишен профилактичен преглед',
  A1M:   'Месечни лабораторни изследвания',
  A2M30: 'Метаболитен панел (5-годишен)',
  A4:    'Гинекологичен скрининг',
  A9M:   'Профилактика за деца',
  A10M:  'Профилактика за възрастни',
  IM07:  'Имунизация',
  V17:   'Планова ваксинация',
};
const planTypeLabel = (type) => PLAN_TYPE_LABEL[type] || 'Профилактичен план';

const CATEGORY_LABEL = { 1: 'Годишен преглед', 2: 'Скрининг', 3: 'Ваксинация' };
const GENDER_LABEL = { MALE: 'Мъж', FEMALE: 'Жена' };

/* ---------------------------------------------------------------- */
/* Date / formatting helpers                                        */
/* ---------------------------------------------------------------- */
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
};

const fmtEgn = (egn) => {
  if (!egn) return '—';
  // 480222 1413 — birthdate block · serial
  return egn.length >= 6 ? `${egn.slice(0, 6)} ${egn.slice(6)}` : egn;
};

const fmtDateTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ageFrom = (iso) => {
  if (!iso) return null;
  const today = new Date();
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  let a = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) a--;
  return a;
};

const daysUntil = (iso) => {
  if (!iso) return Infinity;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return Math.round((new Date(y, m - 1, d) - today) / 86400000);
};

const initials = (name) => {
  const p = (name || '').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[p.length - 1]?.[0] || '')).toUpperCase() || 'П';
};

// Plan status → derived display state (overdue is computed on top of raw status).
const planState = (plan) => {
  const left = daysUntil(plan.toDate);
  if (plan.plannedStatus === 3) return { tone: 'success', label: 'Изпълнено', kind: 'done' };
  if (left < 0)                 return { tone: 'danger',  label: 'Просрочено', kind: 'overdue' };
  if (plan.plannedStatus === 2) return { tone: 'info',    label: 'Частично', kind: 'partial' };
  if (left <= 30)               return { tone: 'warning', label: 'Предстои', kind: 'soon' };
  return { tone: 'neutral', label: 'Планирано', kind: 'planned' };
};

/* ---------------------------------------------------------------- */
/* CountUp — animated counters (dashboard parity)                   */
/* ---------------------------------------------------------------- */
const useCountUp = (target, { duration = 900 } = {}) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (typeof target !== 'number' || target === 0) { setVal(target || 0); return; }
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVal(target); return;
    }
    let raf, start;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      if (start == null) start = now;
      const p = Math.min((now - start) / duration, 1);
      setVal(Math.round(ease(p) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const CountUp = ({ value, suffix = '' }) => {
  const n = useCountUp(value);
  return <>{n}{suffix}</>;
};

const CountFraction = ({ done, total }) => {
  const d = useCountUp(done);
  const t = useCountUp(total);
  return <>{d}/{t}</>;
};

/* ---------------------------------------------------------------- */
/* Activity row                                                     */
/* ---------------------------------------------------------------- */
const Activity = ({ act }) => {
  const code = act.codes && act.codes[0] ? act.codes[0].code : act.nomenclature;
  // No decoded label means the code's nomenclature table isn't loaded in the system.
  const missingNomenclature = act.codes && act.codes[0] && !act.codes[0].label;
  return (
    <li className={`act ${act.done ? 'act-done' : 'act-pending'}`}>
      <span className={`act-mark ${act.done ? 'done' : 'pending'}`}>{act.done ? '✓' : ''}</span>
      <div className="act-body">
        <div className="act-top">
          {code && <span className="code-badge">{code}</span>}
          <span className="act-label">{act.description || code || '—'}</span>
        </div>
        {missingNomenclature && (
          <span className="act-missing-nom">Номенклатурата липсва в системата</span>
        )}
        {act.done && act.nrns && act.nrns.length > 0 && (
          <span className="act-nrn">НРН&nbsp;·&nbsp;{act.nrns.join(', ')}</span>
        )}
      </div>
    </li>
  );
};

/* ---------------------------------------------------------------- */
/* Plan card                                                        */
/* ---------------------------------------------------------------- */
const PlanCard = ({ plan }) => {
  const st = planState(plan);
  const done = plan.activities.filter((a) => a.done).length;
  const total = plan.activities.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const left = daysUntil(plan.toDate);

  const deadlineClass =
    st.kind === 'overdue' ? 'is-overdue' :
    st.kind === 'soon'    ? 'is-urgent'  : '';
  const deadlineText =
    st.kind === 'overdue' ? `Просрочен на ${fmtDate(plan.toDate)}` :
    st.kind === 'soon'    ? `Срок ${fmtDate(plan.toDate)} · след ${left} дн.` :
                            `Срок до ${fmtDate(plan.toDate)}`;

  return (
    <Card flat padding="compact">
      <div className="plan">
        <div className="plan-head">
          <span className="type-badge">{plan.plannedType}</span>
          <h3 className="plan-title">{planTypeLabel(plan.plannedType)}</h3>
          <span className="plan-status">
            {plan.plannedCategory != null && CATEGORY_LABEL[plan.plannedCategory] && (
              <StatusPill tone="neutral" size="sm">{CATEGORY_LABEL[plan.plannedCategory]}</StatusPill>
            )}
            <StatusPill tone={st.tone}>{st.label}</StatusPill>
          </span>
        </div>

        {plan.toDate && (
          <div className={`plan-deadline ${deadlineClass}`}>
            <FeatherIcon icon="clock" size={14} /> {deadlineText}
          </div>
        )}

        <div className="plan-progress">
          <span className="mono" style={{ minWidth: 38 }}><CountFraction done={done} total={total} /></span>
          <span className="progress-track">
            <span
              className={`progress-fill ${st.kind === 'done' ? 'is-done' : ''}`}
              style={{ width: `${pct}%` }}
            />
          </span>
        </div>

        <ul className="acts">
          {plan.activities.map((a, i) => <Activity key={i} act={a} />)}
        </ul>
      </div>
    </Card>
  );
};

/* ---------------------------------------------------------------- */
/* Identity hero header                                             */
/* ---------------------------------------------------------------- */
const IdentityHeader = ({ patient }) => {
  const age = ageFrom(patient.dateOfBirth);
  return (
    <div className="pt-hero">
      <svg className="pt-hero-decor" viewBox="0 0 320 200" fill="none" aria-hidden="true" preserveAspectRatio="xMaxYMid slice">
        <circle cx="250" cy="100" r="120" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="250" cy="100" r="86" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="250" cy="100" r="52" stroke="currentColor" strokeWidth="1.5" />
        <g fill="currentColor">
          {Array.from({ length: 24 }).map((_, i) => {
            const c = i % 6, r = Math.floor(i / 6);
            return <circle key={i} cx={188 + c * 26} cy={36 + r * 26} r="2" />;
          })}
        </g>
      </svg>
      <div className="pt-hero-inner">
        <span className="pt-avatar">{initials(patient.fullName)}</span>
        <div style={{ minWidth: 0 }}>
          <h1 className="pt-name">{patient.fullName}</h1>
          <div className="pt-egn">ЕГН&nbsp;·&nbsp;{fmtEgn(patient.egn)}</div>
          <div className="pt-meta">
            {GENDER_LABEL[patient.gender] && (
              <span className="pt-meta-item">
                <FeatherIcon icon="user" size={14} /> {GENDER_LABEL[patient.gender]}
              </span>
            )}
            <span className="pt-meta-item">
              <FeatherIcon icon="calendar" size={14} /> {patient.birthDisplay}
              {age != null && <> · <CountUp value={age} suffix=" г." /></>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- */
/* Contact card                                                     */
/* ---------------------------------------------------------------- */
const ContactCard = ({ patient, onAdd }) => {
  const Row = ({ glyph, label, value }) => (
    <div className="contact-row">
      <span className="contact-label">{glyph} {label}</span>
      {value
        ? <span className="contact-value">{value}</span>
        : <button className="link-btn" onClick={onAdd}>+ Добави</button>}
    </div>
  );
  return (
    <Section
      title="Контакти"
      icon={<FeatherIcon icon="user" size={18} />}
      subtitle="Въвежда се ръчно от клиниката"
      padding="compact"
    >
      <div className="pt-contact-grid">
        <Row glyph={<FeatherIcon icon="mail" size={14} />} label="Имейл" value={patient.email} />
        <Row glyph={<FeatherIcon icon="phone" size={14} />} label="Телефон" value={patient.phone} />
      </div>
    </Section>
  );
};

/* ---------------------------------------------------------------- */
/* KPI strip                                                        */
/* ---------------------------------------------------------------- */
const StatsStrip = ({ plans }) => {
  const overdue = plans.filter((p) => planState(p).kind === 'overdue').length;
  const allActs = plans.flatMap((p) => p.activities);
  const doneActs = allActs.filter((a) => a.done).length;
  return (
    <div className="pt-stats">
      <Kpi label="Активни планове" value={<CountUp value={plans.length} />} icon={<FeatherIcon icon="shield" size={16} />} />
      <Kpi label="Просрочени" value={<CountUp value={overdue} />} tone={overdue ? 'warning' : 'default'} icon={<FeatherIcon icon="clock" size={16} />} />
      <Kpi label="Дейности" value={<CountFraction done={doneActs} total={allActs.length} />} hint="изпълнени" />
    </div>
  );
};

/* ---------------------------------------------------------------- */
/* Plans section (with first-class empty state)                     */
/* ---------------------------------------------------------------- */
const PlansSection = ({ plans, loading, onSync, lastSyncedAt }) => {
  const syncedText = fmtDateTime(lastSyncedAt);
  const syncLabel = syncedText ? `Синхронизирано на ${syncedText}` : 'Все още не е синхронизирано';
  return (
  <Section
    className="prev-section"
    title="Профилактични планове"
    icon={<FeatherIcon icon="shield" size={18} />}
    actions={
      <div className="prev-actions">
        <span className="plans-sync">{syncLabel}</span>
        <button className="link-btn" onClick={onSync} disabled={loading}>Синхронизирай</button>
      </div>
    }
  >
    {loading ? (
      <div className="text-center py-4"><Spin /></div>
    ) : plans.length === 0 ? (
      <EmptyState
        icon={<FeatherIcon icon="inbox" size={28} />}
        title="Няма профилактични планове"
        description="НЗИС не върна планове за този пациент. Опитайте синхронизация или проверете по-късно."
        action={<button className="link-btn" style={{ fontWeight: 'var(--vc-weight-semibold)' }} onClick={onSync}>Синхронизирай с НЗИС</button>}
      />
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vc-space-3)' }}>
        {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
      </div>
    )}
  </Section>
  );
};

/* ---------------------------------------------------------------- */
/* Vaccinations — always shown in full (no current-window filter)   */
/* ---------------------------------------------------------------- */
const VaccinationsSection = ({ plans, loading }) => (
  <Section
    title="Имунизации"
    icon={<FeatherIcon icon="shield" size={18} />}
    subtitle="Показват се всички, включително просрочените"
  >
    {loading ? (
      <div className="text-center py-4"><Spin /></div>
    ) : plans.length === 0 ? (
      <EmptyState
        icon={<FeatherIcon icon="inbox" size={28} />}
        title="Няма имунизации"
        description="НЗИС не върна планове за имунизация за този пациент."
      />
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vc-space-3)' }}>
        {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
      </div>
    )}
  </Section>
);

/* ---------------------------------------------------------------- */
/* Page                                                             */
/* ---------------------------------------------------------------- */
const PatientProfileMedical = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [preventiveLoading, setPreventiveLoading] = useState(true);
  const [preventivePlans, setPreventivePlans] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  useEffect(() => {
    fetchPatientData();
    fetchPreventivePlans();
  }, [id]);

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const patient = await patientApi.getPatientById(parseInt(id));

      let birthDisplay = '—';
      if (patient.dateOfBirth) birthDisplay = fmtDate(patient.dateOfBirth);
      else if (patient.yearOfBirth) birthDisplay = String(patient.yearOfBirth);

      setPatientData({
        id: patient.id,
        egn: patient.egn,
        fullName: patient.fullName,
        email: patient.email,
        phone: patient.phone,
        gender: patient.gender,
        dateOfBirth: patient.dateOfBirth,
        birthDisplay,
      });
    } catch (error) {
      console.error('Error fetching patient:', error);
      message.error('Грешка при зареждане на пациента');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreventivePlans = async () => {
    try {
      setPreventiveLoading(true);
      const res = await preventiveApi.getPatientPreventivePlans(parseInt(id));
      const sorted = [...(res?.plans || [])].sort((a, b) =>
        (b.toDate || '').localeCompare(a.toDate || '')
      );
      setPreventivePlans(sorted);
      setLastSyncedAt(res?.lastSyncedAt || null);
    } catch (error) {
      console.error('Error fetching preventive plans:', error);
      message.warning('Не можахме да заредим превантивната грижа от НЗИС');
      setPreventivePlans([]);
      setLastSyncedAt(null);
    } finally {
      setPreventiveLoading(false);
    }
  };

  // Vaccinations (category 3) get their own section.
  const vaccinations = useMemo(
    () => preventivePlans.filter((p) => p.plannedCategory === 3),
    [preventivePlans]
  );
  // No filtering for now — show all non-vaccination plans, including overdue ones.
  // (Pending the doctor's guidance on which expired plans should be hidden.)
  const visiblePlans = useMemo(
    () => preventivePlans.filter((p) => p.plannedCategory !== 3),
    [preventivePlans]
  );
  // KPIs reflect everything that's on screen (incl. overdue).
  const statsPlans = useMemo(
    () => [...visiblePlans, ...vaccinations],
    [visiblePlans, vaccinations]
  );

  if (loading) {
    return (
      <PageShell sidebarActive="patient-medical-profile">
        <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
          <Spin size="large" />
        </div>
      </PageShell>
    );
  }

  if (!patientData) return null;

  const breadcrumb = (
    <ul className="breadcrumb">
      <li className="breadcrumb-item"><Link to="/patients">Пациенти</Link></li>
      <li className="breadcrumb-item"><FeatherIcon icon="chevron-right" /></li>
      <li className="breadcrumb-item active">Профил на пациент</li>
    </ul>
  );

  return (
    <PageShell sidebarActive="patient-medical-profile" header={breadcrumb} width="wide">
      <IdentityHeader patient={patientData} />
      <ContactCard patient={patientData} onAdd={() => navigate(`/patients/edit/${patientData.id}`)} />
      <StatsStrip plans={statsPlans} />
      <PlansSection plans={visiblePlans} loading={preventiveLoading} onSync={fetchPreventivePlans} lastSyncedAt={lastSyncedAt} />
      <VaccinationsSection plans={vaccinations} loading={preventiveLoading} />

      <style>{`
        .pt-hero {
          position: relative;
          overflow: hidden;
          background: var(--vc-color-primary);
          color: var(--vc-color-text-on-primary);
          border-radius: var(--vc-radius-xl);
          box-shadow: var(--vc-shadow-md);
          padding: var(--vc-space-6);
        }
        .pt-hero-decor {
          position: absolute; inset: 0 0 0 auto;
          width: 320px; pointer-events: none;
          color: #fff; opacity: .10;
          -webkit-mask-image: linear-gradient(90deg, transparent, #000 55%);
                  mask-image: linear-gradient(90deg, transparent, #000 55%);
        }
        .pt-hero-inner { position: relative; display: flex; align-items: center; gap: var(--vc-space-4); }
        .pt-avatar {
          flex: none;
          width: 56px; height: 56px;
          border-radius: var(--vc-radius-pill);
          background: rgba(255,255,255,.16);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.28);
          color: #fff;
          display: grid; place-items: center;
          font-weight: var(--vc-weight-bold);
          font-size: var(--vc-text-lg);
          letter-spacing: .04em;
        }
        .pt-name {
          margin: 0;
          font-size: var(--vc-text-2xl);
          font-weight: var(--vc-weight-semibold);
          line-height: var(--vc-leading-tight);
          letter-spacing: .01em;
          color: #fff;
        }
        .pt-egn {
          font-family: var(--vc-font-mono);
          font-size: var(--vc-text-md);
          letter-spacing: .06em;
          color: rgba(255,255,255,.82);
          margin-top: 3px;
        }
        .pt-meta {
          display: flex; flex-wrap: wrap;
          gap: var(--vc-space-2) var(--vc-space-5);
          margin-top: var(--vc-space-3);
        }
        .pt-meta-item {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: var(--vc-text-sm);
          font-weight: var(--vc-weight-medium);
          color: rgba(255,255,255,.92);
        }
        .pt-meta-item svg { color: rgba(255,255,255,.7); vertical-align: middle; }
        .mono { font-family: var(--vc-font-mono); }

        /* Sync date on top, sync button under it (right-aligned on desktop) */
        .prev-actions { display: flex; flex-direction: column; align-items: flex-end; gap: var(--vc-space-1); }
        .plans-sync { font-size: var(--vc-text-sm); color: var(--vc-color-text-subtle); white-space: nowrap; }

        /* Mobile: center the title and the date/button stack */
        @media (max-width: 767px) {
          .prev-section .vc-section__head { flex-direction: column; align-items: center; text-align: center; }
          .prev-section .vc-section__title { justify-content: center; }
          .prev-section .prev-actions { align-items: center; }
        }

        /* KPI strip */
        .pt-stats { display: grid; grid-template-columns: 1fr; gap: var(--vc-space-3); }

        /* Contact rows */
        .pt-contact-grid { display: grid; grid-template-columns: 1fr; }
        .contact-row {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--vc-space-3);
          padding: var(--vc-space-2) 0;
        }
        .contact-row + .contact-row { border-top: 1px solid var(--vc-color-border-soft); }
        .contact-label {
          display: flex; align-items: center; gap: var(--vc-space-2);
          font-size: var(--vc-text-sm);
          color: var(--vc-color-text-muted);
        }
        .contact-value { font-size: var(--vc-text-md); font-weight: var(--vc-weight-medium); }
        .link-btn {
          appearance: none; border: none; background: none; cursor: pointer;
          font-family: inherit; font-size: var(--vc-text-sm);
          font-weight: var(--vc-weight-medium);
          color: var(--vc-color-primary);
          padding: 2px 4px; border-radius: var(--vc-radius-sm);
        }
        .link-btn:hover { background: var(--vc-color-primary-soft); }
        .link-btn:disabled { opacity: .5; cursor: default; }

        /* Plan card */
        .plan { display: flex; flex-direction: column; gap: var(--vc-space-3); }
        .plan-head {
          display: flex; flex-wrap: wrap; align-items: center;
          gap: var(--vc-space-2) var(--vc-space-3);
        }
        .type-badge {
          font-family: var(--vc-font-mono);
          font-size: var(--vc-text-sm);
          font-weight: var(--vc-weight-bold);
          letter-spacing: .06em;
          color: var(--vc-color-primary);
          background: var(--vc-color-primary-soft);
          padding: 3px 9px;
          border-radius: var(--vc-radius-sm);
          line-height: 1.3;
        }
        .plan-title {
          font-size: var(--vc-text-md);
          font-weight: var(--vc-weight-semibold);
          margin: 0;
          flex: 1 1 180px;
          min-width: 0;
        }
        .plan-status { margin-left: auto; display: flex; align-items: center; gap: var(--vc-space-2); flex-wrap: wrap; }
        .plan-deadline {
          display: flex; align-items: center; gap: 6px;
          font-size: var(--vc-text-sm);
          color: var(--vc-color-text-muted);
          padding-top: 2px;
        }
        .plan-deadline svg { vertical-align: middle; }
        .plan-deadline.is-urgent { color: var(--vc-color-warning); font-weight: var(--vc-weight-medium); }
        .plan-deadline.is-overdue { color: var(--vc-color-danger); font-weight: var(--vc-weight-medium); }

        .plan-progress {
          display: flex; align-items: center; gap: var(--vc-space-2);
          font-size: var(--vc-text-xs);
          color: var(--vc-color-text-muted);
        }
        .progress-track {
          flex: 1 1 auto; height: 5px; border-radius: var(--vc-radius-pill);
          background: var(--vc-color-border); overflow: hidden;
        }
        .progress-fill { height: 100%; border-radius: var(--vc-radius-pill); background: var(--vc-color-primary); transition: width .6s ease; }
        .progress-fill.is-done { background: var(--vc-color-success); }

        /* Activities */
        .acts { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
        .act {
          display: flex; align-items: flex-start; gap: var(--vc-space-3);
          padding: var(--vc-space-2) 0;
        }
        .act + .act { border-top: 1px solid var(--vc-color-border-soft); }
        .act-mark {
          flex: none; width: 20px; height: 20px; margin-top: 1px;
          border-radius: var(--vc-radius-sm);
          display: grid; place-items: center;
          font-size: 12px; line-height: 1;
        }
        .act-mark.done { background: var(--vc-color-success-bg); color: var(--vc-color-success); }
        .act-mark.pending { border: 1.5px dashed var(--vc-color-border); }
        .act-body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
        .act-top { display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--vc-space-2); }
        .code-badge {
          font-family: var(--vc-font-mono);
          font-size: var(--vc-text-xs);
          color: var(--vc-color-text-soft);
          background: var(--vc-color-neutral-bg);
          padding: 1px 6px; border-radius: var(--vc-radius-sm);
          flex: none;
        }
        .act-label { font-size: var(--vc-text-base); color: var(--vc-color-text); line-height: var(--vc-leading-normal); }
        .act-pending .act-label { color: var(--vc-color-text-soft); }
        .act-missing-nom { font-size: var(--vc-text-xs); color: var(--vc-color-text-subtle); }
        .act-nrn {
          font-family: var(--vc-font-mono);
          font-size: var(--vc-text-xs);
          color: var(--vc-color-text-subtle);
          display: inline-flex; align-items: center; gap: 5px;
        }

        /* Responsive — match the design's 600px breakpoints */
        @media (min-width: 600px) {
          .pt-stats { grid-template-columns: repeat(3, 1fr); }
          .pt-contact-grid { grid-template-columns: 1fr 1fr; column-gap: var(--vc-space-6); }
          .pt-contact-grid .contact-row + .contact-row { border-top: none; }
        }
        @media (max-width: 600px) {
          /* Status pills align left under the title (not pushed to the right). */
          .plan-status { margin-left: 0; }

          /* Activity row: checkmark + code on the top line; description spans
             full width and starts at the far left, directly under the checkmark. */
          .act {
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: var(--vc-space-2);
            row-gap: 3px;
            align-items: start;
          }
          .act-mark { grid-column: 1; grid-row: 1; }
          .act-body { display: contents; }
          .act-top { display: contents; }
          .code-badge { grid-column: 2; grid-row: 1; justify-self: start; align-self: center; }
          /* Each of these takes its own full-width row, in source order. */
          .act-label, .act-missing-nom, .act-nrn { grid-column: 1 / -1; }
        }
        @media (max-width: 480px) {
          .pt-hero { padding: var(--vc-space-5); }
          .pt-name { font-size: var(--vc-text-xl); }
          .pt-hero-decor { width: 220px; }
        }
      `}</style>
    </PageShell>
  );
};

export default PatientProfileMedical;
